import React, { useEffect, useRef, useState } from "react";
import {
  createMenuItem,
  hasFeature,
  deleteMenuItem,
  fetchMenu,
  getUser,
  getCurrency,
  resolveImageUrl,
  updateMenuItem,
  uploadMenuImage,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import MenuItemForm from "../components/menu/MenuItemForm";
import MenuItemTable from "../components/menu/MenuItemTable";
import { resizeImage } from "../components/menu/resizeImage";
import { useT } from "../i18n/useT";

// AM/OM are machine drafts (2026-06) — pending native review
const STRINGS = {
  en: {
    title: "Menu",
    errLoad: "Could not load menu.",
    errSave: "Save failed.",
    errUpload: "Upload failed.",
    confirmDelete: "Delete this menu item?",
    print_menu: "Print menu (PDF)",
    print_popup: "Please allow pop-ups to print the menu.",
  },
  am: {
    title: "ሜኑ",
    errLoad: "ሜኑን መጫን አልተቻለም።",
    errSave: "ማስቀመጥ አልተሳካም።",
    errUpload: "መስቀል አልተሳካም።",
    confirmDelete: "ይህን የሜኑ ዕቃ ማጥፋት ይፈልጋሉ?",
    print_menu: "ሜኑ አትም (PDF)",
    print_popup: "ሜኑን ለማተም እባክዎ ብቅ-ባይ መስኮቶችን ይፍቀዱ።",
  },
  om: {
    title: "Meenuu",
    errLoad: "Meenuu fe'uun hin danda'amne.",
    errSave: "Olkaa'uun hin milkoofne.",
    errUpload: "Ol'kaa'uun hin milkoofne.",
    confirmDelete: "Meeshaa meenuu kana balleessuu barbaaddaa?",
    print_menu: "Meenuu maxxansi (PDF)",
    print_popup: "Meenuu maxxansuuf maaloo foddaa balballoomfataa hayyami.",
  },
};

const EMPTY = {
  station: "",
  name: "",
  name_am: "",
  name_om: "",
  name_sid: "",
  name_ti: "",
  name_nl: "",
  price: "",
  cost: "",
  category: "Food",
  description: "",
  available: true,
  image_url: "",
  recipe: "",
  modifiers_text: "",  // raw "name|price" lines, parsed on save
  dietary_tags: [],
  // Chef-curation: when true the item appears in the "Recommended"
  // strip at the top of the customer menu. The optional note is shown
  // as a small caption under the item.
  is_chef_pick: false,
  chef_pick_note: "",
};

// Modifiers are entered as "Name|Price" lines (price is optional, defaults to 0).
// Empty lines are ignored. Examples:
//   No onion
//   Extra cheese|1.5
//   Spicy
function parseModifiers(text) {
  if (!text) return null;
  const out = [];
  for (const raw of text.split(/\r?\n/)) {
    const line = raw.trim();
    if (!line) continue;
    const [namePart, pricePart] = line.split("|").map((s) => s.trim());
    if (!namePart) continue;
    const price = pricePart ? parseFloat(pricePart) : 0;
    out.push({ name: namePart, price: isNaN(price) ? 0 : price });
  }
  return out.length ? out : null;
}
function modifiersToText(mods) {
  if (!mods || !mods.length) return "";
  return mods.map((m) => (m.price ? `${m.name}|${m.price}` : m.name)).join("\n");
}

// Default language sets per country -- mirror the customer app's
// fallback when the admin hasn't set an explicit supported_languages
// list. Single source of truth shared with the customer-app's
// COUNTRY_DEFAULTS in MenuPage.js.
const COUNTRY_DEFAULTS = {
  ET: ["en", "am", "om"],
  NL: ["en", "nl"],
  OTHER: ["en"],
};

function effectiveLanguages(user) {
  // Admin's explicit list wins. When null/empty, fall back to the
  // country defaults so an unconfigured tenant still sees its
  // historical language inputs and nothing breaks during migration.
  const explicit = Array.isArray(user?.supported_languages)
    ? user.supported_languages
    : null;
  if (explicit && explicit.length > 0) return explicit;
  const country = (user?.country || "ET").toUpperCase();
  return COUNTRY_DEFAULTS[country] || COUNTRY_DEFAULTS.OTHER;
}

export default function ManagerMenuPage() {
  const { lang } = useT();
  const t = (k) => (STRINGS[lang] && STRINGS[lang][k]) || STRINGS.en[k] || k;
  const [items, setItems] = useState([]);

  const toggleAvailability = async (item) => {
    const next = !item.available;
    setItems((arr) => arr.map((x) => (x.id === item.id ? { ...x, available: next } : x)));
    try {
      await updateMenuItem(item.id, { available: next });
    } catch (e) {
      setItems((arr) => arr.map((x) => (x.id === item.id ? { ...x, available: !next } : x)));
      setError('Could not update availability.');
    }
  };
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState(null);
  const [uploadingFor, setUploadingFor] = useState(null);
  const fileInputRef = useRef(null);
  // Picked-but-not-yet-uploaded image for the create / edit form.
  // Holds {file: File, previewUrl: string} or null.
  const [pendingImage, setPendingImage] = useState(null);
  const [removeExistingImage, setRemoveExistingImage] = useState(false);
  const formImageInputRef = useRef(null);

  // Which translation inputs to show is admin-controlled per tenant.
  // Examples:
  //   ["en","am"]            -> show only Amharic input
  //   ["en","am","om"]       -> show Amharic + Afaan Oromo (Addis default)
  //   ["en","am","sid"]      -> show Amharic + Af Sidama (Sintalle/Hawassa)
  //   ["en","nl","am"]       -> show Dutch + Amharic (NL diaspora)
  const langs = effectiveLanguages(getUser());
  const showAm = langs.includes("am");
  const showOm = langs.includes("om");
  const showSid = langs.includes("sid");
  const showTi = langs.includes("ti");
  const showNl = langs.includes("nl");

  const load = () =>
    fetchMenu()
      .then(setItems)
      .catch((e) => setError(e?.response?.data?.detail || t("errLoad")));

  useEffect(() => { load(); }, []);

  const reset = () => {
    setForm(EMPTY);
    setEditingId(null);
    if (pendingImage?.previewUrl) URL.revokeObjectURL(pendingImage.previewUrl);
    setPendingImage(null);
    setRemoveExistingImage(false);
    if (formImageInputRef.current) formImageInputRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        cost: form.cost !== "" && form.cost != null ? parseFloat(form.cost) : null,
        name_am: form.name_am || null,
        name_om: form.name_om || null,
        name_sid: form.name_sid || null,
        name_ti: form.name_ti || null,
        name_nl: form.name_nl || null,
        is_chef_pick: !!form.is_chef_pick,
        chef_pick_note: form.chef_pick_note || null,
        recipe: form.recipe || null,
        modifiers: parseModifiers(form.modifiers_text),
        dietary_tags: form.dietary_tags && form.dietary_tags.length
          ? form.dietary_tags
          : null,
        // If the manager picked "remove image" in edit mode, push an
        // empty string so the backend clears the column. Otherwise we
        // leave image_url alone here; the actual file upload happens
        // separately below and updates the URL on the server side.
        ...(editingId && removeExistingImage ? { image_url: null } : {}),
      };
      delete payload.modifiers_text;
      let savedId = editingId;
      if (editingId) {
        await updateMenuItem(editingId, payload);
      } else {
        const created = await createMenuItem(payload);
        savedId = created?.id;
      }
      // After create/update, if a fresh image was picked, upload it
      // against the saved item's id. We do this AFTER the save so the
      // item exists and the upload route can find it.
      if (pendingImage?.file && savedId) {
        await uploadMenuImage(savedId, pendingImage.file);
      }
      reset();
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || t("errSave"));
    }
  };

  const edit = (item) => {
    setForm({
      name: item.name,
      name_am: item.name_am || "",
      name_om: item.name_om || "",
      name_sid: item.name_sid || "",
      name_ti: item.name_ti || "",
      name_nl: item.name_nl || "",
      is_chef_pick: !!item.is_chef_pick,
      chef_pick_note: item.chef_pick_note || "",
      price: String(item.price),
      cost: item.cost != null ? String(item.cost) : "",
      category: item.category || "Food",
      station: item.station || "",
      description: item.description || "",
      available: item.available,
      dietary_tags: Array.isArray(item.dietary_tags) ? item.dietary_tags : [],
      image_url: item.image_url || "",
      recipe: item.recipe || "",
      modifiers_text: modifiersToText(item.modifiers),
    });
    setEditingId(item.id);
  };

  const remove = async (id) => {
    if (!window.confirm(t("confirmDelete"))) return;
    await deleteMenuItem(id);
    load();
  };

  const triggerUpload = (id) => {
    setUploadingFor(id);
    fileInputRef.current?.click();
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !uploadingFor) return;
    try {
      // Resize large phone photos client-side so we don't hit the
      // backend's 4 MB upload limit. resizeImage returns the original
      // file unchanged if it's already small.
      const resized = await resizeImage(file);
      await uploadMenuImage(uploadingFor, resized);
      load();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || t("errUpload"));
    } finally {
      setUploadingFor(null);
    }
  };

  // Open a clean, print-formatted menu (number + name + price + description,
  // grouped by category — no availability/edit controls) for print or Save-as-PDF.
  // The #ID matches the number shown on the Captain Order tablet, so a guest can
  // read a number off the printed menu and the waiter finds it instantly.
  const printMenu = () => {
    const esc = (s) => String(s == null ? "" : s).replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));
    const currency = getCurrency();
    const venue = (getUser() || {}).name || "";
    const groups = {};
    (items || []).forEach((m) => { const c = m.category || "Other"; (groups[c] = groups[c] || []).push(m); });
    const cats = Object.keys(groups).sort((a, b) => a.localeCompare(b));
    const parts = [];
    cats.forEach((c) => {
      parts.push(`<h2>${esc(c)}</h2>`);
      groups[c].forEach((m) => {
        const num = esc(m.code != null ? m.code : m.id);
        const img = resolveImageUrl(m.image_url);
        const thumb = img
          ? `<img class="thumb" src="${esc(img)}" alt="" />`
          : `<div class="thumb ph">${esc((m.name || "•").slice(0, 1))}</div>`;
        parts.push(
          `<div class="item">${thumb}<div class="txt">` +
          `<div class="row"><span class="id">#${num}</span><span class="name">${esc(m.name)}</span>` +
          `<span class="price">${esc(formatPrice(m.price, currency))}</span></div>` +
          (m.description ? `<div class="desc">${esc(m.description)}</div>` : "") +
          `</div></div>`
        );
      });
    });
    const w = window.open("", "_blank", "width=820,height=1000");
    if (!w) { setError(t("print_popup")); return; }
    w.document.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(venue)} — Menu</title><style>` +
      `*{font-family:-apple-system,Segoe UI,Roboto,sans-serif;box-sizing:border-box}body{margin:32px;color:#1f2933;-webkit-print-color-adjust:exact;print-color-adjust:exact}` +
      `h1{font-size:24px;color:#063b48;margin:0 0 2px;text-align:center}.sub{text-align:center;color:#5c7c7f;font-size:12px;letter-spacing:.15em;text-transform:uppercase;margin-bottom:20px}` +
      `h2{font-size:15px;color:#008080;border-bottom:2px solid #008080;padding-bottom:3px;margin:18px 0 8px;text-transform:uppercase;letter-spacing:.04em}` +
      `.item{display:flex;gap:10px;align-items:flex-start;margin:0 0 11px}` +
      `.thumb{width:56px;height:56px;object-fit:cover;border-radius:7px;flex-shrink:0;background:#f0ece6}` +
      `.thumb.ph{display:flex;align-items:center;justify-content:center;color:#b9b2a4;font-size:22px;font-weight:700}` +
      `.txt{flex:1;min-width:0}.row{display:flex;align-items:baseline;gap:8px}.id{font-weight:700;color:#063b48;min-width:34px}.name{font-weight:600;flex:1}.price{font-weight:700;color:#063b48;white-space:nowrap}` +
      `.desc{font-size:12px;color:#5c7c7f;margin:2px 0 0}@media print{body{margin:14mm}.noprint{display:none}}` +
      `.btn{background:#008080;color:#fff;border:none;padding:8px 16px;border-radius:6px;font-weight:700;cursor:pointer}</style></head><body>` +
      `<h1>${esc(venue)}</h1><div class="sub">Menu</div>` +
      parts.join("") +
      `<p class="noprint" style="text-align:center;margin-top:22px"><button class="btn" onclick="window.print()">🖨 Print / Save as PDF</button></p>` +
      `</body></html>`
    );
    w.document.close(); w.focus();
    setTimeout(() => { try { w.print(); } catch (e) { /* user can click the button */ } }, 400);
  };

  return (
    <>
      <div className="space-between" style={{ alignItems: "center" }}>
        <h1 style={{ margin: 0 }}>{t("title")}</h1>
        <button className="btn secondary small" onClick={printMenu}>🖨 {t("print_menu")}</button>
      </div>
      {error && <div className="error">{error}</div>}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: "none" }}
        onChange={onFileChosen}
      />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 20 }}>
        <MenuItemForm
          form={form}
          setForm={setForm}
          editingId={editingId}
          onSubmit={submit}
          onCancel={reset}
          pendingImage={pendingImage}
          setPendingImage={setPendingImage}
          removeExistingImage={removeExistingImage}
          setRemoveExistingImage={setRemoveExistingImage}
          formImageInputRef={formImageInputRef}
          setError={setError}
          showStation={hasFeature("stations")}
          showAm={showAm}
          showOm={showOm}
          showSid={showSid}
          showTi={showTi}
          showNl={showNl}
        />

        <MenuItemTable
          items={items}
          uploadingFor={uploadingFor}
          onUpload={triggerUpload}
          onEdit={edit}
          onDelete={remove}
          onToggleAvailability={toggleAvailability}
        />
      </div>
    </>
  );
}
